import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import BrowseTools from './pages/BrowseTools';
import ToolDetail from './pages/ToolDetail';
import ToolReviews from './pages/ToolReviews';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import AddTool from './pages/AddTool';
import Categories from './pages/Categories';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import HelpCenter from './pages/HelpCenter';
import SafetyTips from './pages/SafetyTips';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Insurance from './pages/Insurance';
import History from './pages/History';
import Settings from './pages/Settings';
import { ToolsProvider } from './context/ToolsContext';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ManageTools from './pages/ManageTools';
import RequestBook from './pages/RequestBook';
import ViewConversation from './pages/ViewConversation';
import EditTool from './pages/EditTool';
import BorrowedTools from './pages/BorrowedTools';
import Favorites from './pages/Favorites';
import Notifications from './pages/Notifications';
import { FavoritesProvider } from './context/FavoritesContext';
import EmailVerification from './pages/EmailVerification';
import NewUserConfirmed from './pages/NewUserConfirmed';
import AllLendBookings from './pages/AllLendBookings';
import AllBorrowBookings from './pages/AllBorrowBookings';
import AllMessageThreads from './pages/AllMessageThreads';

import ScrollToTop from './components/layout/ScrollToTop';

// Help Articles
import CreateAccount from './pages/help/CreateAccount';
import FindTool from './pages/help/FindTool';
import FirstBooking from './pages/help/FirstBooking';
import UnderstandPricing from './pages/help/UnderstandPricing';
import BookingProcess from './pages/help/BookingProcess';
import PickupReturn from './pages/help/PickupReturn';
import ExtendRental from './pages/help/ExtendRental';
import CancellationPolicy from './pages/help/CancellationPolicy';
import PaymentManagement from './pages/help/PaymentManagement';
import SecurePayment from './pages/help/SecurePayment';
import PaymentProblems from './pages/help/PaymentProblems';
import DisputeSupport from './pages/help/DisputeSupport';
import InsuranceCoverage from './pages/help/InsuranceCoverage';
import ToolDamage from './pages/help/ToolDamage';
import ContactOwners from './pages/help/ContactOwners';

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center min-h-screen text-gray-600">Der opstod en uventet fejl. Prøv at genindlæse siden.</div>}>
    <FavoritesProvider>
      <ToolsProvider>
        <Router>
          <ScrollToTop />
          <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/browse" element={<BrowseTools />} />
                <Route path="/tool/:id" element={<ToolDetail />} />
                <Route path="/tool/:id/reviews" element={<ToolReviews />} />
                <Route path="/tool/:id/book" element={<RequestBook />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/profile/:id" element={<Profile />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/email-verification" element={<EmailVerification />} />
                <Route path="/new-user-confirmed" element={<NewUserConfirmed />} />
                <Route path="/add-tool" element={<AddTool />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<HelpCenter />} />
                {/* Help Articles */}
                <Route path="/help/create-account" element={<CreateAccount />} />
                <Route path="/help/find-tool" element={<FindTool />} />
                <Route path="/help/first-booking" element={<FirstBooking />} />
                <Route path="/help/understand-pricing" element={<UnderstandPricing />} />
                <Route path="/help/booking-process" element={<BookingProcess />} />
                <Route path="/help/pickup-return" element={<PickupReturn />} />
                <Route path="/help/extend-rental" element={<ExtendRental />} />
                <Route path="/help/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/help/payment-management" element={<PaymentManagement />} />
                <Route path="/help/secure-payment" element={<SecurePayment />} />
                <Route path="/help/payment-problems" element={<PaymentProblems />} />
                <Route path="/help/dispute-support" element={<DisputeSupport />} />
                <Route path="/help/insurance-coverage" element={<InsuranceCoverage />} />
                <Route path="/help/tool-damage" element={<ToolDamage />} />
                <Route path="/help/contact-owners" element={<ContactOwners />} />
                <Route path="/safety" element={<SafetyTips />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/insurance" element={<Insurance />} />
                <Route path="/history" element={<History />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/users/password/edit" element={<ResetPassword />} />
                <Route path="/manage-tools" element={<ManageTools />} />
                <Route path="/conversation/:id" element={<ViewConversation />} />
                <Route path="/edit-tool/:id" element={<EditTool />} />
                <Route path="/borrowed-tools" element={<BorrowedTools />} />
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/all-lend-bookings" element={<AllLendBookings />} />
                <Route path="/all-borrow-bookings" element={<AllBorrowBookings />} />
                <Route path="/all-message-threads" element={<AllMessageThreads />} />
              </Routes>
            </main>
            <Footer />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#ffffff',
                  color: '#1f2937',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                  borderRadius: '0.75rem',
                  padding: '12px 14px',
                },
                success: {
                  iconTheme: {
                    primary: '#0f766e',
                    secondary: '#ecfeff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#dc2626',
                    secondary: '#fef2f2',
                  },
                },
              }}
            />
          </div>
        </Router>
      </ToolsProvider>
    </FavoritesProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;